import { useEffect, useState } from 'react'
import { LoaderFunctionArgs, redirect, useLoaderData } from 'react-router-dom'
import { ButtonAnchor, PageHeading } from '../../components/common'
import { queueFlashAfterNavigation } from '../../components/common/Toasts'
import { getMe } from '../../api/me'
import {
  NewPaymentScreen,
  PendingPayment,
  UpgradePaymentScreen,
  fetchPaymentScreen,
  fetchPendingPayment,
} from '../../api/payments'
import { trackEvent } from '../../lib/config'
import styles from './NewPaymentPage.module.scss'
import { hcCurrency } from '../../lib/currency'
import { formatDate, lastDayOfMonth } from '../../lib/dates'
import { PendingPaymentModal } from './PendingPaymentModal'

const MONTH_OPTIONS: readonly { value: number; label: string }[] = [
  { value: 1, label: '1 mes' },
  { value: 2, label: '2 meses' },
  { value: 3, label: '3 meses' },
  { value: 12, label: '1 año (10% dcto)' },
]

function planLabel(plan: { name: string; price: number }): string {
  return `${hcCurrency(plan.price)} / mes - ${plan.name}`
}

/**
 * Both cards leave the SPA immediately, so the choice goes out by beacon: a
 * batched XHR would be cancelled by the navigation it is describing.
 */
function PaymentMethodCards({ periodText, onWebpay, electronicTransferPath, onElectronicTransfer, methodProperties }: {
  periodText: string
  onWebpay: () => void
  electronicTransferPath: string
  onElectronicTransfer?: () => void
  methodProperties: Record<string, unknown>
}) {
  const selectMethod = (method: 'webpay' | 'electronic_transfer') =>
    trackEvent('Payment Method Selected', { method, ...methodProperties }, { transport: 'sendBeacon' })

  return (
    <div className={`col-xs-12 col-md-10 col-md-offset-1 ${styles.box}`}>
      <div className="card">
        <div className="card-body">
          <div className="col-xs-10">
            <h5 className="card-title">Pago Online</h5>
            <p className="card-text payment-period">{periodText}</p>
          </div>
          <div className="col-xs-2">
            <ButtonAnchor
              href="#"
              id="webpay_button"
              onClick={(event) => {
                event.preventDefault()
                selectMethod('webpay')
                onWebpay()
              }}
            >
              Ir
            </ButtonAnchor>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="col-xs-10">
            <h5 className="card-title">Transferencia electrónica</h5>
            <p className="card-text">Puede tardar más debido a que requiere confirmación manual</p>
          </div>
          <div className="col-xs-2">
            <ButtonAnchor
              href={electronicTransferPath}
              onClick={(event) => {
                selectMethod('electronic_transfer')
                if (onElectronicTransfer) {
                  event.preventDefault()
                  onElectronicTransfer()
                }
              }}
            >
              Ir
            </ButtonAnchor>
          </div>
        </div>
      </div>
    </div>
  )
}

function NewPaymentScreenView({ data }: { data: NewPaymentScreen }) {
  const [planCode, setPlanCode] = useState(data.plans[0]?.code ?? '')
  const [months, setMonths] = useState(1)

  const monthlyPrice = data.plans_by_code[planCode]?.price ?? data.monthly_price
  const discount = months === 12 ? 0.1 : 0
  const amount = monthlyPrice * months * (1 - discount)

  const now = new Date()
  const beginDate = lastDayOfMonth(new Date(now.getFullYear(), now.getMonth() - 1, 5))
  const endDate = lastDayOfMonth(new Date(now.getFullYear(), now.getMonth() - 1 + months, 5))

  const selectPlan = (nextPlanCode: string, nextMonths: number) =>
    trackEvent('Payment Plan Selected', { plan: nextPlanCode, months: nextMonths, has_annual_discount: nextMonths === 12 })

  return (
    <>
      <div className="col-xs-12">
        <PageHeading id="sizes">Selecciona tu forma de pago</PageHeading>

        <div className={styles.attribute}>
          <span>Plan escogido</span>
          <select
            id="plan_selector"
            className={`form-control ${styles.periodSelector}`}
            value={planCode}
            onChange={(event) => {
              setPlanCode(event.target.value)
              selectPlan(event.target.value, months)
            }}
          >
            {data.plans.map((plan) => (
              <option key={plan.code} value={plan.code}>
                {planLabel(plan)}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.attribute} id="payment-months">
          <span>Paga</span>
          <select
            id="months_selector"
            className={`form-control ${styles.periodSelector}`}
            value={months}
            onChange={(event) => {
              setMonths(Number(event.target.value))
              selectPlan(planCode, Number(event.target.value))
            }}
          >
            {MONTH_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span>de historia clínica por</span>
          <span className="value">{hcCurrency(amount)}</span>
        </div>
        {data.freemium && <p className={styles.centered}>Para actualizar a premium, solo debes realizar tu primer pago</p>}
        <p className={styles.centered} />
      </div>

      <PaymentMethodCards
        periodText={`Pago valido para el periodo desde ${formatDate(beginDate)} hasta ${formatDate(endDate)}`}
        onWebpay={() => window.location.assign(`${data.webpay_path}?months=${months}&plan=${planCode}`)}
        electronicTransferPath={data.electronic_transfer_path}
        methodProperties={{ plan: planCode, months, amount }}
      />
      <div className={`row ${styles.centered}`}>
        <ButtonAnchor href={data.profile_path}>Volver a mi perfil</ButtonAnchor>
      </div>
    </>
  )
}

function UpgradePaymentScreenView({ data }: { data: UpgradePaymentScreen }) {
  const [planCode, setPlanCode] = useState(data.plans[0]?.code ?? '')
  const paid = data.current_paid_payment
  const months = paid.remaining_periods
  const total = data.plans_by_code[planCode]?.price ?? 0

  const goTo = (path: string) => window.location.assign(`${path}?months=${months}&plan=${planCode}`)

  return (
    <>
      <div className="col-xs-12">
        <PageHeading id="sizes">Mejora de plan</PageHeading>

        <div className={styles.attribute}>
          <p className="subtitle">Información de último pago</p>
          <table className="table">
            <tbody>
              <tr>
                <td>Monto pagado</td>
                <td id="paid-amount">{hcCurrency(paid.amount)}</td>
              </tr>
              <tr>
                <td>Desde</td>
                <td>{paid.begin_date}</td>
              </tr>
              <tr>
                <td>Hasta</td>
                <td>{paid.end_date}</td>
              </tr>
              <tr>
                <td>Periodos restantes</td>
                <td>{paid.remaining_periods}</td>
              </tr>
              <tr>
                <td>Abonado</td>
                <td>{hcCurrency(paid.credit_amount_for_update_payment)}</td>
              </tr>
            </tbody>
          </table>

          <p className="subtitle">Selección del nuevo plan</p>

          <table className="table">
            <tbody>
              <tr>
                <td>Nuevo Plan</td>
                <td>
                  <select
                    id="plan_selector"
                    className={`form-control ${styles.periodSelector}`}
                    style={{ width: '100%', background: 'white' }}
                    value={planCode}
                    onChange={(event) => {
                      setPlanCode(event.target.value)
                      trackEvent('Payment Plan Selected', { plan: event.target.value, months, has_annual_discount: months === 12 })
                    }}
                  >
                    {data.plans.map((plan) => (
                      <option key={plan.code} value={plan.code}>
                        {planLabel(plan)}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
              <tr>
                <td>Monto a pagar</td>
                <td className="final-amount">{hcCurrency(total * months - paid.amount)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className={styles.attribute} id="payment-months">
          <p className="subtitle">{`Actualizar cuenta hasta el ${paid.end_date}`}</p>
        </div>
      </div>

      <PaymentMethodCards
        periodText={`Pago valido para el periodo desde ${paid.begin_date} hasta ${paid.end_date}`}
        onWebpay={() => goTo(data.webpay_path)}
        electronicTransferPath={data.electronic_transfer_path}
        onElectronicTransfer={() => goTo(data.electronic_transfer_path)}
        methodProperties={{ plan: planCode, months, amount: total * months - paid.amount }}
      />
      <div className={`row ${styles.centered}`}>
        <ButtonAnchor href={data.profile_path}>Volver a mi perfil</ButtonAnchor>
      </div>
    </>
  )
}

type NewPaymentData = {
  readonly screen: NewPaymentScreen | UpgradePaymentScreen
  readonly pendingPayment: PendingPayment | null
  readonly patientsCount: number | undefined
}

export async function loader({ params }: LoaderFunctionArgs): Promise<Response | NewPaymentData> {
  const healthCenterId = Number(params.healthCenterId)
  const [screen, me] = await Promise.all([fetchPaymentScreen(healthCenterId), getMe()])
  if ('redirect' in screen) {
    queueFlashAfterNavigation({ notice: screen.notice, warning: screen.warning })
    return redirect(screen.redirect)
  }
  const patientsCount = me.health_centers.find((center) => center.id === healthCenterId)?.patients_count
  const pendingPayment = screen.screen === 'new' ? (await fetchPendingPayment(screen.check_pending_payment_path)).pending_payment : null
  return { screen, pendingPayment, patientsCount }
}

export default function NewPaymentPage() {
  const { screen, pendingPayment: loadedPendingPayment, patientsCount } = useLoaderData() as NewPaymentData
  const [pendingPayment, setPendingPayment] = useState<PendingPayment | null>(() => loadedPendingPayment)

  useEffect(() => {
    trackEvent('Payment Screen Viewed', { screen: screen.screen, plan: screen.plans[0]?.code ?? null, patients_count: patientsCount })
  }, [])

  return (
    <div className="col-md-8 col-md-offset-2">
      {screen.screen === 'new' ? <NewPaymentScreenView data={screen} /> : <UpgradePaymentScreenView data={screen} />}
      {pendingPayment && <PendingPaymentModal payment={pendingPayment} onDismiss={() => setPendingPayment(null)} />}
    </div>
  )
}
